import { useI18n } from '../../../shared/i18n';
import { localePath } from '../../../shared/i18n/routing';
import { cn } from '../../../shared/lib/cn';
import type { PublicProject } from '../publicApi';
import { AttributionBadge } from './AttributionBadge';

export interface ProjectCardProps {
  project: PublicProject;
  className?: string;
}

/**
 * A portfolio listing entry.
 *
 * An image, a title and a meta row inside a bordered, elevated card that
 * lifts on hover — the Bitwize direction (docs/design-directions.md is being
 * superseded by the Bitwize brand reference) frames the screenshot rather
 * than leaving it borderless.
 *
 * The whole card is one link with one accessible name. `AttributionBadge` is
 * visible text inside it, not a second interactive element — nesting a link
 * or button inside this one would make it invalid HTML and give a
 * keyboard user two stops for what is conceptually one card.
 *
 * `hero_image` carries explicit `width`/`height` from the API, which is most
 * of what prevents layout shift; the blurhash placeholder those dimensions
 * are paired with is HSQ-079 (M4) — full responsive image delivery.
 */
export function ProjectCard({ project, className }: ProjectCardProps) {
  const { locale, formatNumber } = useI18n();

  return (
    <a
      href={localePath(locale, `/projects/${project.slug}`)}
      className={cn(
        'hs-card group flex flex-col overflow-hidden rounded-[var(--hs-card-radius)]',
        'transition-all duration-[--hs-duration-base] ease-out hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md',
        className,
      )}
    >
      {/*
        A gradient rather than a flat sunken surface: with no hero image this
        is what the visitor sees, and an empty slab reads as a failed load
        where a gradient reads as a card that simply has no screenshot yet.
        It is a colourless wash, not the mock's brand blue — see
        `--hs-project-placeholder-bg` for why.

        Full-bleed to the card's edges, as the mock has it — the image is the
        top of the card, not a picture pasted inside a padded one.
      */}
      <div className="aspect-[16/9] overflow-hidden bg-[image:var(--hs-project-placeholder-bg)]">
        {project.hero_image ? (
          <img
            src={project.hero_image.url}
            alt={project.hero_image.alt}
            width={project.hero_image.width ?? undefined}
            height={project.hero_image.height ?? undefined}
            loading="lazy"
            decoding="async"
            className={cn(
              'h-full w-full object-cover',
              'transition-transform duration-[--hs-duration-slow]',
              'group-hover:scale-[1.02]',
            )}
          />
        ) : null}
      </div>

      {/*
        The mock's order: the meta line first, then the title, then the
        summary — the card reads "what kind of work, when" before it reads
        the name, which is how a portfolio grid is actually scanned.

        The attribution badge sits in that line rather than below the summary
        (LEG-04: the disclosure is visible text on every listing card), and
        the year keeps the locale's own numbering through `formatNumber`.
      */}
      <div className="flex flex-col gap-1.5 p-[var(--hs-card-padding)]">
        <div className="hs-meta flex flex-wrap items-center gap-2 text-accent">
          <AttributionBadge
            type={project.attribution.type}
            label={project.attribution.badge_label}
          />
          {project.year !== null ? (
            <span>{formatNumber(project.year, { useGrouping: false })}</span>
          ) : null}
        </div>
        <h3 className="line-clamp-2 text-lg font-semibold text-text">{project.title}</h3>
        {project.summary ? (
          <p className="line-clamp-2 text-sm text-text-muted">{project.summary}</p>
        ) : null}
      </div>
    </a>
  );
}
